import React from 'react';
import { useParams } from 'react-router-dom';
import { StyleSheet, View } from './components/base_components';

import Breadcrumb from './components/breadcrumb';
import BoxPropList from './components/box_prop_list';
import ProgressSpinner from './components/progress_spinner';
import StandardBoxList from './components/standard_box_list';

import OrgStore from './stores/org_store';
import InvoiceStore from './stores/invoice_store';
import UserStore from './stores/user_store';

import { dateFormat, moneyFormat, moneyLongFormat } from './tools/util';

const styles = StyleSheet.create({
  orgInvoiceScreen: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  spinner: {
    margin: '3rem',
    alignSelf: 'center',
  },
});

const METRIC_MAP = {
  USER: 'User Licenses',
  STORAGE: 'Network Storage',
};
const SUFFIX_MAP = {
  USER: 'user-day',
  STORAGE: 'GB-day',
};

export default function OrgInvoiceScreen() {
  const params = useParams();
  const invoice_id = params?.invoice_id ?? '';
  const org_id = UserStore.useOrg()?.org_id;
  const org = OrgStore.useOrg(org_id);
  const invoice = InvoiceStore.useInvoice(invoice_id);

  React.useEffect(() => {
    OrgStore.fetch(org_id);
    InvoiceStore.fetchInvoice({ org_id, invoice_id });
  }, [org_id, invoice_id]);

  let content: any = null;
  if (!org || !invoice) {
    content = <ProgressSpinner style={styles.spinner} color='#555' />;
  } else {
    const rows = invoice.invoice_line_list.map((line) => {
      const start = dateFormat(line.start_date, 'yyyy-MM-dd');
      const end = dateFormat(line.end_date, 'yyyy-MM-dd');
      const desc = `${METRIC_MAP[line.metric]}: ${start} to ${end}`;
      return [
        desc,
        moneyLongFormat(line.unit_price_usd) + '/' + SUFFIX_MAP[line.metric],
        line.count + ' ' + SUFFIX_MAP[line.metric],
        moneyFormat(line.subtotal_usd),
      ];
    });
    const payment_rows = invoice.payment_list.map((payment) => [
      dateFormat(payment.payment_time),
      `${payment.card_brand} •••• ${payment.card_last4}`,
      payment.payment_status,
      moneyFormat(payment.amount_usd),
    ]);

    const name = `Invoice #${invoice.invoice_id.slice(0, 8)}`;
    content = (
      <>
        <Breadcrumb
          list={[
            { text: org?.org_name, url: `/org` },
            { text: 'Invoices', url: `/org/invoice` },
            name,
          ]}
        />
        <BoxPropList
          title={name}
          columns={[
            [
              {
                label: 'Invoice ID',
                value: invoice.invoice_id,
              },
              {
                label: 'Invoice Date',
                value: dateFormat(invoice.invoice_date, 'yyyy-MM-dd'),
              },
            ],
            [
              {
                label: 'Invoice Total',
                value: moneyFormat(invoice.total_usd),
              },
              {
                label: 'Invoice Status',
                value: invoice.invoice_status,
              },
            ],
          ]}
        />
        <StandardBoxList
          title='Invoice Detail'
          headers={['Description', 'Unit Price', 'Amount', 'Subtotal']}
          sizes={[3]}
          rows={rows}
        />
        <StandardBoxList
          title='Payments'
          headers={['Date', 'Payment Method', 'Status', 'Amount']}
          rows={payment_rows}
        />
      </>
    );
  }
  return <View style={styles.orgInvoiceScreen}>{content}</View>;
}
